import Box from "@/common/components/Box";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import ResumeItem from "@/common/components/ResumeItem";
import { formatCurrency, formatUnit } from "@/common/utils/formatters";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import { MaintenanceDirectiveObjectiveData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type";
import { isExecutionEntryProposalContent } from "@/features/maintenance/features/workflows/types/forwardings/establishmentChoiceEntries/proposalContents/proposalContent.type";
import { WrenchIcon } from "lucide-react";
import { useMemo } from "react";
import ProposalAwareResumeItem from "../ProposalAwareResumeItem";

interface ExecutionEntryObjectiveActivitiesProps {
  objective: MaintenanceDirectiveObjectiveData;
  dataUpdatedAt?: number;
}

const ExecutionEntryObjectiveActivities = ({
  objective,
  dataUpdatedAt,
}: ExecutionEntryObjectiveActivitiesProps) => {
  const { forwarding } = useForwardingTabContext();

  const proposalContent = forwarding
    ?.establishment_choice_entry
    ?.actual_proposal
    ?.proposal_content;

  const proposalStatus = forwarding
    ?.establishment_choice_entry
    ?.actual_proposal
    ?.status;

  const activityModifications = isExecutionEntryProposalContent(proposalContent, proposalStatus)
    ? proposalContent.activity_modifications
    : undefined;

  const modificationByActivityId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof activityModifications>[number]>();

    activityModifications?.forEach((modification) => {
      map.set(modification.objective_activity_id, modification);
    });

    return map;
  }, [activityModifications]);

  const activitiesCountDisplay = useMemo(() => {
    const count = objective.execution_activities?.length || 0;

    if (count === 0) {
      return 'Nenhum serviço';
    }

    if (count === 1) {
      return '1 Serviço';
    }

    return `${count} Serviços`;
  }, [dataUpdatedAt]);

  const totalValueSum = useMemo(() => {
    return objective.execution_activities?.reduce((prev, curr) => {
      const modification = modificationByActivityId.get(curr.id);
      const value = modification?.new_total_value ?? curr.total_value;
      return prev + Number(value);
    }, 0) || 0;
  }, [dataUpdatedAt, modificationByActivityId]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <p className="font-bold text-base">
            Serviços
          </p>

          <div className="flex items-center gap-0.5 text-xs">
            <span>
              {activitiesCountDisplay}
            </span>

            <span>
              •
            </span>

            <span className="text-valid">
              {formatCurrency(totalValueSum)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {objective.execution_activities?.map((diagnosticActivity) => {
          const modification = modificationByActivityId.get(diagnosticActivity.id);

          return (
            <Box key={diagnosticActivity.id} className="p-4">
              <div className="flex gap-3">
                <div className="flex gap-4">
                  <ImageCarousel
                    files={diagnosticActivity.images?.map((image) => ({
                      id: image.id,
                      file: image?.url,
                    })) || []}
                    Placeholder={(
                      <ImageWithPlaceholder
                        className="size-20 rounded-md"
                        iconClassName="size-10"
                        placeholderIcon={WrenchIcon}
                      />
                    )}
                    className="size-20"
                    showRemoveButton={false}
                    showExpandButton
                  />

                  <div className="flex flex-col gap-2">
                    <p className="text-base font-bold">
                      {diagnosticActivity?.display_name || '--'}
                    </p>

                    <div className="flex items-center flex-wrap gap-3">
                      <ProposalAwareResumeItem
                        label="Valor unt."
                        originalValue={Number(diagnosticActivity?.unit_price) || 0}
                        newValue={
                          modification?.new_unit_price != null
                            ? Number(modification.new_unit_price)
                            : null
                        }
                        format={formatCurrency}
                      />

                      <p className="text-sm">x</p>

                      {diagnosticActivity.unit_for_quantity === 'minutes' ? (
                        <ResumeItem
                          className="text-sm"
                          label="Tempo de execução"
                          value={formatUnit(diagnosticActivity.quantity, 'hours')}
                        />
                      ) : (
                        <ResumeItem
                          className="text-sm"
                          label="Unidades"
                          value={formatUnit(diagnosticActivity.quantity, 'unit')}
                        />
                      )}

                      <p className="text-sm">=</p>

                      <ProposalAwareResumeItem
                        label="Valor total"
                        originalValue={Number(modification?.original_total_value ?? diagnosticActivity?.total_value) || 0}
                        newValue={
                          modification?.new_total_value != null
                            ? Number(modification.new_total_value)
                            : null
                        }
                        differencePercentage={modification?.difference_percentage}
                        format={formatCurrency}
                      />

                      <ProposalAwareResumeItem
                        label="Garantia"
                        originalValue={diagnosticActivity.warranty_in_days}
                        newValue={modification?.new_warranty_in_days}
                        format={(value) => `${value} Dias`}
                        higherIsBetter
                      />
                    </div>

                    <ResumeItem
                      className="text-sm"
                      label="Descrição"
                      value={diagnosticActivity.description}
                    />
                  </div>
                </div>
              </div>
            </Box>
          );
        })}
      </div>
    </div>
  );
}

export default ExecutionEntryObjectiveActivities;
